import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Environment } from '@core/services/common';
import { AnalyticsConfigService } from '@core/services/core/analytics/analytics-configuration';
import { Platform } from '@ionic/angular';
import { enc, MD5 } from 'crypto-js';
import {
  getCookie,
  getReferrer,
  getUserAgent,
  getUserLanguage,
  hasAdblock,
  isPreproduction,
  isProduction
} from 'src/app/core/utils/utils';
import { PoliciesContextService } from '../../context/policies-context.service';
import { AuthMirrorService } from '../auth/auth-mirror.service';
import { ContextService } from '../context.service';
import { LogService } from '../log.service';

export const METS_APP_PREFIX = 'es:mapfre:app:mets';
export const METS_WEB_PREFIX = 'es:mapfre:area de clientes';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsContext {
  // yes means repeat
  public newRepeat: 'yes' | 'no' = 'no';

  private _hasAdblock: string;

  private optionalFields = {};
  private pageTitle = {
    url: '',
    slug: ''
  };
  private modalTitle = {
    slug: ''
  };

  constructor(
    private log: LogService,
    private router: Router,
    private authMirror: AuthMirrorService,
    private policiesContext: PoliciesContextService,
    private platform: Platform,
    private analyticsConfigService: AnalyticsConfigService,
    private ctx: ContextService,
  ) {}

  get googleAnalyticsCookie() {
    return this.analyticsConfigService.getConfig().googleAnalyticsCookie;
  }
  get pageType() {
    return this.analyticsConfigService.getConfig().pageType;
  }

  setPageTitle({ url, slug }: { url?: string; slug: string }) {
    this.pageTitle = {
      ...this.pageTitle,
      url,
      slug
    };
  }

  getPageTitle(titleScreen: string): string {
    return (this.platform.is('cordova')
      ? METS_APP_PREFIX
      : METS_WEB_PREFIX) + titleScreen;
  }


  setModalTitle({ slug }) {
    this.modalTitle = { slug };
  }

  clearModalTitle() {
    this.modalTitle = {
      slug: ''
    };
  }

  async getFields(id?) {
    const url = id || this.router.url;
    const mandatoryFields: any = await this.obtainMandatoryFields();
    const optionalFields = this.optionalFields[url]
      ? await this.resolve(this.optionalFields[url].resolvers)
      : {};

    mandatoryFields.Pagetitle = this.calculatePageTitle(optionalFields);
    mandatoryFields.Screenname = mandatoryFields.Pagetitle;

    return { mandatoryFields, optionalFields };
  }

  activateDecorator(url: string, resolvers: (() => Promise<any>)[]) {
    this.optionalFields[url] = { resolvers };
  }

  deactivateDecorator(url: string) {
    delete this.optionalFields[url];
  }

  private resolve(resolvers: (() => Promise<any>)[]) {
    return Promise.all(resolvers.map(resolver => resolver())).then(resolved =>
      Object.assign({}, ...resolved)
    );
  }

  private async obtainMandatoryFields() {
    const urlFields = this.calculateUrlFields();
    const web = {
      cod_ric: this.getRicCode(),
      CD21_environment: isProduction()
        ? 'production'
        : isPreproduction()
        ? 'staging'
        : 'development',
      CD24_platform: this.getPlatform(),
      CD25_user_modality: this.getUserType(),
      CD28_channel: 'area de clientes',
      ...urlFields,
      CD37_url: this.router.url,
      CD38_referrer: getReferrer(),
      CD40_page_type:
        this.getPageType() ||
        this.log.w(
          this,
          `Analytics: Page type not defined for url ${this.router.url}`
        ),
      CD41_language: getUserLanguage(),
      CD43_site_version: Environment.config().version,
      CD44_user_agent: getUserAgent(),
      CD52_new_repeat: this.newRepeat,
      CD53_client_id: getCookie(this.googleAnalyticsCookie),
      CD54_user_id: this.getUserId(),
      CD55_adblock: await this.hasAdblock(),
      ...this.policiesContext.getAnalytics()
    };

    const { CD53_client_id, CD55_adblock, ...app } = web;

    return this.platform.is('cordova') ? app : web;
  }

  private calculateUrlFields() {
    const urlSections = this.router.url.split('/', 9);
    urlSections.shift();
    return {
      CD29_section1: urlSections[0],
      CD30_section2: urlSections[1],
      CD31_section3: urlSections[2],
      CD32_section4: urlSections[3],
      CD33_section5: urlSections[4],
      CD34_section6: urlSections[5],
      CD35_section7: urlSections[6],
      CD36_section8: urlSections[7]
    };
  }

  private calculatePageTitle({ CD132_process_name, basic_prefix }) {
    const metsPrefix = this.platform.is('cordova') ? METS_APP_PREFIX : METS_WEB_PREFIX;
    const prefix = basic_prefix || metsPrefix;
    const url =
      this.modalTitle.slug ||
      this.pageTitle.slug ||
      this.pageTitle.url;
    const pageTitle = url.split('?')[0].split('/');

    return [prefix, CD132_process_name, ...pageTitle].filter(Boolean).join(':');
  }

  private getUserId() {
    const { user } = this.authMirror.authService.getState();
    const { email } = { ...user };

    return email ? MD5(email).toString(enc.Base64) : '';
  }

  private getUserType() {
    if (this.authMirror.authService.getState().isLogged) {
      return this.authMirror.authService.checkUserIsCompany() ? 'empresa' : 'particular';
    } else {
      return '';
    }
  }

  private getPlatform() {
    return this.ctx.isApp ? 'app' : 'web';
  }

  private getPageType() {
    const pageType = this.pageType.find(
      ([regExp, _]) => !!this.router.url.match(regExp)
    );
    return (pageType && pageType[1]) || '';
  }

  private getRicCode() {
    const { user } = this.authMirror.authService.getState();
    const { userId } = user || ({} as any);

    return userId || '';
  }

  private async hasAdblock() {
    if (this._hasAdblock) return this._hasAdblock;

    this._hasAdblock = (await hasAdblock())
      ? 'adblock detected'
      : 'adblock not detected';
    return this._hasAdblock;
  }
}
